import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { getExpiredDate } from './tokens';
import { getLoginTimezoneOffset } from './timezoneOffset';
import { loginSuccess } from '../actions/usersActions';

const authHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export const postAdminAuth = (path, body = {}) => (
  axios.post(`${BASE_URL.url}${path}`, body, { headers: authHeaders })
);

export const isAdminSession = (data) => Boolean(
  data && typeof data === 'object' && data.access_token
);

export const withTimezoneOffset = (body = {}) => {
  const next = { ...body };
  const timezoneOffset = getLoginTimezoneOffset();
  if (timezoneOffset != null) next.timezoneOffset = timezoneOffset;
  return next;
};

/** Применить сессию админа (как после /login/admin). */
export const applyAdminSession = (dispatch, session) => {
  if (!isAdminSession(session)) return null;
  const data = { ...session };
  data.access_token_expire = getExpiredDate(data);
  const responseLike = { data, status: 200 };
  if (dispatch) dispatch(loginSuccess(responseLike));
  return data;
};

export const getAdminAuthErrorPayload = (error) => {
  const data = error?.response?.data;
  return data && typeof data === 'object' && !Array.isArray(data) ? data : null;
};

export const getAdminAuthError = (error, fallback = '') => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data && typeof data === 'object') {
    if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
    if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
  }
  if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
  return fallback;
};

export const getAdminAuthRetryAfter = (error) => {
  const data = error?.response?.data;
  const value = Number(data?.retry_after);
  return Number.isFinite(value) && value > 0 ? value : 0;
};

export const isNotAdminMergeOffer = (error) => {
  const payload = getAdminAuthErrorPayload(error);
  return Boolean(
    error?.response?.status === 403
    && payload
    && (payload.error === 'not_admin' || payload.code === 'not_admin')
    && payload.can_merge === true
  );
};

export const normalizeMergeOffer = (payload = {}) => {
  const killIds = Array.isArray(payload.kill_ids)
    ? payload.kill_ids
    : (Array.isArray(payload.merged_ids) ? payload.merged_ids : []);
  const killEmails = Array.isArray(payload.kill_emails_masked)
    ? payload.kill_emails_masked
    : [];
  return {
    can_merge: Boolean(payload.can_merge),
    keep_id: payload.keep_id ?? null,
    kill_ids: killIds,
    phone_masked: payload.phone_masked || '',
    keep_email_masked: payload.keep_email_masked || null,
    kill_emails_masked: killEmails,
    message: payload.message || '',
  };
};

export const adminAuthCheck = (login) => (
  postAdminAuth(API_URLS.adminAuthCheck, { login: String(login || '').trim() })
);

export const adminAuthLogin = (login, password) => (
  postAdminAuth(API_URLS.login, withTimezoneOffset({
    login: String(login || '').trim(),
    password: String(password || ''),
  }))
);

export const adminAuthTelegramSend = (payload) => (
  postAdminAuth(API_URLS.adminAuthTelegramSend, payload)
);

export const adminAuthTelegramVerify = (payload) => (
  postAdminAuth(API_URLS.adminAuthTelegramVerify, payload)
);

export const adminAuthSmsSend = (payload) => (
  postAdminAuth(API_URLS.adminAuthSmsSend, payload)
);

export const adminAuthSmsVerify = (payload) => (
  postAdminAuth(API_URLS.adminAuthSmsVerify, payload)
);

export const adminAuthComplete = (payload) => (
  postAdminAuth(API_URLS.adminAuthComplete, withTimezoneOffset(payload))
);

export const adminAuthEmailSendCode = (email) => (
  postAdminAuth(API_URLS.emailSendCode, {
    email: String(email || '').trim(),
    purpose: 'admin_setup',
  })
);

export const adminAuthForgot = (login) => (
  postAdminAuth(API_URLS.adminAuthForgot, { login: String(login || '').trim() })
);

export const adminAuthForgotSms = (payload) => (
  postAdminAuth(API_URLS.adminAuthForgotSms, payload)
);

export const adminAuthForgotVerify = (payload) => (
  postAdminAuth(API_URLS.adminAuthForgotVerify, payload)
);

export const adminAuthForgotReset = (payload) => (
  postAdminAuth(API_URLS.adminAuthForgotReset, withTimezoneOffset(payload))
);

/** Превью или склейка клиентского дубля в админский аккаунт. */
export const adminAuthMergeDuplicate = (login, { confirm = false } = {}) => (
  postAdminAuth(API_URLS.adminAuthMergeDuplicate, {
    login: String(login || '').trim(),
    ...(confirm ? { confirm: true } : {}),
  })
);

export const ADMIN_AUTH_PUBLIC_PATHS = [
  API_URLS.login,
  API_URLS.adminAuthCheck,
  API_URLS.adminAuthTelegramSend,
  API_URLS.adminAuthTelegramVerify,
  API_URLS.adminAuthSmsSend,
  API_URLS.adminAuthSmsVerify,
  API_URLS.adminAuthComplete,
  API_URLS.emailSendCode,
  API_URLS.adminAuthForgot,
  API_URLS.adminAuthForgotSms,
  API_URLS.adminAuthForgotVerify,
  API_URLS.adminAuthForgotReset,
  API_URLS.adminAuthMergeDuplicate,
];

export const isAdminAuthPublicUrl = (url = '') => (
  ADMIN_AUTH_PUBLIC_PATHS.some((path) => String(url).indexOf(path) !== -1)
);
